import { useMediaQuery } from "../ui/useMediaQuery";
import { PrivateImage } from "../ui/PrivateImage";
import { privateObjectUrl, revokePrivateObjectUrl } from "./privateFiles";
import { useEffect, useMemo, useRef, useState } from "react";
import { Archive, RotateCcw, Star, Maximize2, X, ChevronLeft, File, FileText, Folder, Grid2X2, Image as ImageIcon, List, Search, Video } from "lucide-react";
import { cn } from "../ui/cn";
import { formatBytes } from "./format";
import { fetchFile } from "./fileDownloads";
import { errorMessage } from "./format";
import { FileSaveButton } from "./FileSaveButton";
import { paginateWordTables } from "./wordPagination";
type TeamFile = { id: string; messageId: string; filename: string; mediaType: string; size: number; createdAt: number; folder: string; url: string | null; current?: boolean; archivedAt?: number };
export type PreviewFile = Pick<TeamFile, "id" | "filename" | "mediaType" | "size" | "url">;
type Operation = "archive" | "restore" | "set-current" | "clear-current";

type Props = {
  loading: boolean;
  files: readonly TeamFile[];
  currentFile?: TeamFile | null;
  archived?: boolean;
  canManage?: boolean;
  onManage?: (id: string, operation: Operation) => Promise<unknown>;
  onToggleArchive?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
};

type ViewMode = "list" | "grid";

const naturalSort = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function extension(filename: string): string {
  const suffix = filename.split(".").pop();
  return suffix === undefined || suffix === filename ? "FILE" : suffix.toLocaleUpperCase();
}

function isWordFile(file: PreviewFile): boolean {
  return file.filename.toLocaleLowerCase().endsWith(".docx");
}

function isPdf(file: PreviewFile): boolean {
  return file.mediaType === "application/pdf" || file.filename.toLocaleLowerCase().endsWith(".pdf");
}

function FileKindIcon({ file, size = 18 }: { file: PreviewFile; size?: number }) {
  if (file.mediaType.startsWith("image/")) return <ImageIcon size={size} />;
  if (file.mediaType.startsWith("video/")) return <Video size={size} />;
  if (isPdf(file) || isWordFile(file)) return <FileText size={size} />;
  return <File size={size} />;
}

function WordPreview({ file }: { file: PreviewFile }) {
  const previewRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(true);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    const controller = new AbortController();
    const preview = previewRef.current;
    if (preview === null || file.url === null) {
      setState("error");
      return () => { controller.abort(); };
    }
    preview.replaceChildren();
    setState("loading");
    void (async () => {
      try {
        const response = await fetch(file.url ?? "", { signal: controller.signal });
        if (!response.ok) throw new Error("preview unavailable");
        const document = await response.blob();
        if (controller.signal.aborted) return;
        const { renderAsync } = await import("docx-preview");
        await renderAsync(document, preview, preview, {
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          ignoreFonts: false,
          ignoreHeight: false,
          ignoreWidth: false,
          inWrapper: true,
          renderFooters: true,
          renderHeaders: true
        });
        controller.signal.throwIfAborted();
        setPage(0);
        setPageCount(Math.max(1, preview.querySelectorAll("section.docx").length));
        setState("ready");
      } catch {
        if (!controller.signal.aborted) setState("error");
      }
    })();
    return () => {
      controller.abort();
      preview.replaceChildren();
    };
  }, [file.id, file.url]);

  useEffect(() => {
    const container = containerRef.current;
    const preview = previewRef.current;
    if (!container || !preview || state !== "ready") return;
    paginateWordTables(preview);
    setPageCount(Math.max(1, preview.querySelectorAll("section.docx").length));
    preview.querySelectorAll<HTMLElement>("section.docx").forEach((section, index) => { section.style.display = index === page ? "" : "none"; });
    const resize = () => {
      preview.style.transform = "none";
      const width = preview.scrollWidth;
      const height = preview.scrollHeight;
      const scale = fit ? Math.min(1, (container.clientWidth - 24) / width, (container.clientHeight - 24) / height) : 1;
      preview.style.transform = `scale(${Math.max(scale, 0.05)})`;
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    return () => { observer.disconnect(); };
  }, [state, fit, page]);

  return (
    <div className="team-file-docx"><div className="file-zoom"><button onClick={() => { setFit(value => !value); }}>{fit ? "Actual size" : pageCount > 1 ? "Fit page" : "Fit document"}</button>{pageCount > 1 ? <><button aria-label="Previous page" disabled={page === 0} onClick={() => { setPage(value => value - 1); }}>←</button><span>Page {page + 1} of {pageCount}</span><button aria-label="Next page" disabled={page + 1 >= pageCount} onClick={() => { setPage(value => value + 1); }}>→</button></> : <span>{fit ? "Entire document" : "Scroll to read"}</span>}</div><div ref={containerRef} className={cn("word-viewport", fit && "word-viewport--fit")}>
      {state === "loading" ? <div className="team-file-preview-status"><span className="preview-skeleton" /><span className="preview-skeleton preview-skeleton--short" />Preparing Word preview…</div> : null}
      {state === "error" ? <div className="team-file-preview-status"><FileText size={34} /><strong>Preview unavailable</strong><span>You can still open or download the original Word file.</span></div> : null}
      <div ref={previewRef} className={cn("team-file-docx__canvas", state !== "ready" && "is-hidden")} />
    </div></div>
  );
}

function ResolvedPreview({ file }: { file: PreviewFile }) {
  if (file.url === null) {
    return <div className="team-file-preview-status"><FileKindIcon file={file} size={34} /><strong>File unavailable</strong><span>The original file could not be opened.</span></div>;
  }
  if (file.mediaType.startsWith("image/")) {
    return <div className="team-file-native-preview"><img src={file.url} alt={file.filename} /></div>;
  }
  if (file.mediaType.startsWith("video/")) {
    return <div className="team-file-native-preview"><video src={file.url} controls preload="metadata"><track kind="captions" /></video></div>;
  }
  if (isPdf(file)) {
    return <iframe referrerPolicy="no-referrer" className="team-file-pdf" src={`${file.url}#view=Fit&navpanes=0`} title={`Preview of ${file.filename}`} />;
  }
  if (isWordFile(file)) return <WordPreview file={file} />;
  return <div className="team-file-preview-status"><FileKindIcon file={file} size={34} /><strong>No preview for this format</strong><span>Open the original file to view it.</span></div>;
}

function FilePreview({ file }: { file: PreviewFile }) {
  const [resolved, setResolved] = useState<PreviewFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    let localUrl: string | undefined;
    setResolved(null); setError(null);
    void fetchFile(file, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      localUrl = privateObjectUrl(result.blob);
      setResolved({ ...file, filename: result.filename, mediaType: result.mediaType, url: localUrl });
    }).catch((caught: unknown) => { if (!controller.signal.aborted) setError(errorMessage(caught)); });
    return () => { controller.abort(); if (localUrl) revokePrivateObjectUrl(localUrl); };
  }, [file]);
  if (error) return <div className="team-file-preview-status" role="alert">{error}</div>;
  if (!resolved) return <div className="team-file-preview-status">Preparing preview…</div>;
  return <ResolvedPreview file={resolved} />;
}

function FileThumbnail({ file }: { file: TeamFile }) {
  if (file.url !== null && file.mediaType.startsWith("image/")) return <PrivateImage src={file.url} alt="" loading="lazy" />;
  return <span className={`team-file-type team-file-type--${extension(file.filename).toLocaleLowerCase()}`}><FileKindIcon file={file} size={18} /><small>{extension(file.filename)}</small></span>;
}

export function TeamFilesLibrary({ loading, files, currentFile = null, archived = false, canManage = false, onManage, onToggleArchive, hasMore = false, loadingMore = false, onLoadMore }: Props) {
  const mobile = useMediaQuery("(max-width: 1000px)");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [fullscreen, setFullscreen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const manage = async (file: TeamFile, operation: Operation) => {
    if (operation === "archive" && !window.confirm(`Archive ${file.filename}? It will move out of active files for everyone. You can restore it from Archived.`)) return false;
    setBusy(true); setError(null);
    try { await onManage?.(file.id, operation); return true; } catch (caught) { setError(errorMessage(caught)); return false; } finally { setBusy(false); }
  };
  const folders = useMemo(() => {
    const counts = new Map<string, number>();
    for (const file of files) {
      if (file.folder) counts.set(file.folder, (counts.get(file.folder) ?? 0) + 1);
    }
    return [...counts].sort(([left], [right]) => naturalSort.compare(left, right));
  }, [files]);
  const [folder, setFolder] = useState("");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<ViewMode>("list");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const visibleFiles = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return files
      .filter((file) => (normalizedQuery ? file.filename.toLocaleLowerCase().includes(normalizedQuery) : file.folder === folder))
      .sort((left, right) => naturalSort.compare(left.filename, right.filename));
  }, [files, folder, query]);
  const selected = currentFile?.id === selectedId && !archived ? currentFile : visibleFiles.find((file) => file.id === selectedId) ?? visibleFiles[0] ?? null;
  const checkedFiles = visibleFiles.filter(file => checked.has(file.id));
  const manageSelected = async () => {
    const operation = archived ? "restore" : "archive";
    if (!window.confirm(`${archived ? "Restore" : "Archive"} ${checkedFiles.length} selected files for everyone? Originals are preserved.`)) return;
    setBusy(true); setError(null);
    try {
      for (const file of checkedFiles) await onManage?.(file.id, operation);
      setChecked(new Set());
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (selected !== null && selected.id !== selectedId) setSelectedId(selected.id);
  }, [selected, selectedId]);

  const openFile = (file: TeamFile) => {
    setSelectedId(file.id);
    if (mobile) { setFullscreen(true); dialogRef.current?.showModal(); }
  };

  const openFolder = (nextFolder: string) => {
    setFolder(nextFolder);
    setQuery("");
    setSelectedId(null);
  };

  if (loading) {
    return <div className="team-files-loading" aria-label="Loading files"><span /><span /><span /></div>;
  }

  return (
    <div className="team-files-library">
      <div className="team-files-toolbar">
        <label className="team-files-search"><Search size={16} /><span className="sr-only">Search Team Files</span><input type="search" value={query} placeholder={`Search ${files.length}${hasMore ? " loaded" : ""} files`} onChange={(event) => { setQuery(event.target.value); setSelectedId(null); }} /></label>
        <button className="file-tool-button" type="button" onClick={onToggleArchive} aria-pressed={archived}><Archive size={16} />{archived ? "Back to active" : "Archived"}</button>
        <div className="team-files-view-toggle" aria-label="File view">
          <button type="button" className={view === "list" ? "is-active" : ""} aria-label="List view" aria-pressed={view === "list"} onClick={() => { setView("list"); }}><List size={17} /></button>
          <button type="button" className={view === "grid" ? "is-active" : ""} aria-label="Grid view" aria-pressed={view === "grid"} onClick={() => { setView("grid"); }}><Grid2X2 size={16} /></button>
        </div>
      </div>
      {error ? <p className="file-error" role="alert">{error}</p> : null}
      {!archived && currentFile ? <button className="current-schedule" onClick={() => { openFile(currentFile); }}><Star size={17} /><span><strong>Current schedule</strong><small>{currentFile.filename}</small></span><span>View →</span></button> : null}
      {!archived && !currentFile && canManage ? <p className="file-help">Select the approved schedule, then choose “Make current schedule.”</p> : null}
      <div className="team-files-content">
        <section className="team-files-browser" aria-label="Team Files browser">
          <div className="team-files-heading">
            {folder ? <button type="button" onClick={() => { openFolder(""); }}><ChevronLeft size={16} /> Team Files</button> : <div><strong>{archived ? "Archived files" : "Team Files"}</strong><span>{files.length} files · {folders.length} {folders.length === 1 ? "folder" : "folders"}</span></div>}
            {folder ? <div><strong>{folder}</strong><span>{visibleFiles.length} files</span></div> : null}
          </div>
          {!query && !folder && folders.length > 0 ? (
            <div className="team-files-folders">
              {folders.map(([name, count]) => <button key={name} type="button" onClick={() => { openFolder(name); }}><span><Folder size={20} /></span><div><strong>{name}</strong><small>{count} items</small></div></button>)}
            </div>
          ) : null}
          <div className={cn("team-files-items", view === "grid" && "team-files-items--grid")}>
            {canManage && visibleFiles.length > 0 ? <div className="file-bulk-controls"><label><input type="checkbox" aria-label="Select listed files except current schedule" checked={visibleFiles.some(file => !file.current) && visibleFiles.filter(file => !file.current).every(file => checked.has(file.id))} onChange={event => { setChecked(event.target.checked ? new Set(visibleFiles.filter(file => !file.current).map(file => file.id)) : new Set()); }} />Select listed files (except current)</label>{checkedFiles.length > 0 ? <button disabled={busy} onClick={() => { void manageSelected(); }}>{archived ? "Restore" : "Archive"} selected ({checkedFiles.length})</button> : null}</div> : null}
            {visibleFiles.map((file) => (
              <div key={file.id} className="file-selectable-row">
              {canManage ? <input type="checkbox" aria-label={`Select ${file.filename}`} checked={checked.has(file.id)} onChange={event => { setChecked(previous => { const next = new Set(previous); if (event.target.checked) next.add(file.id); else next.delete(file.id); return next; }); }} /> : null}
              <button type="button" className={cn("team-file-row", selected?.id === file.id && "is-selected")} onClick={() => { openFile(file); }}>
                <span className="team-file-thumbnail"><FileThumbnail file={file} /></span>
                <span className="team-file-row__details"><strong>{file.filename}</strong><small>{extension(file.filename)} · {formatBytes(file.size)}</small></span>
              </button>
              </div>
            ))}
          </div>
          {hasMore || loadingMore ? <button className="file-tool-button" disabled={loadingMore} onClick={onLoadMore}>{loadingMore ? "Loading…" : "Load more files"}</button> : null}
          {visibleFiles.length === 0 ? <div className="team-files-empty"><Search size={24} /><strong>No matching files</strong><span>Try a different file name.</span></div> : null}
        </section>
        {!mobile ? <aside className={cn("team-file-preview", selected !== null && "is-open")} aria-label="File preview">
          {selected === null ? <div className="team-file-preview-status"><FileText size={34} /><strong>Select a file</strong><span>Its preview will appear here.</span></div> : (
            <>
              <header><div><strong>{selected.filename}</strong><span>{extension(selected.filename)} · {formatBytes(selected.size)}</span></div></header>
              <div className="file-controls">
                <FileSaveButton file={selected} />
                <button disabled={!selected.url} onClick={() => { setFullscreen(true); dialogRef.current?.showModal(); }}><Maximize2 size={15} />Full screen</button>
                {canManage ? <>
                  {!archived ? <button disabled={busy || !selected.url} onClick={() => { void manage(selected, selected.current ? "clear-current" : "set-current"); }}><Star size={15} />{selected.current ? "Unmark current" : "Make current schedule"}</button> : null}
                  <button disabled={busy} onClick={() => { void manage(selected, archived ? "restore" : "archive"); }}>{archived ? <RotateCcw size={15} /> : <Archive size={15} />}{archived ? "Restore" : "Archive"}</button>
                </> : null}
              </div>
              <div className="team-file-preview__body"><FilePreview file={selected} /></div>
            </>
          )}
        </aside> : null}
      </div>
      <dialog ref={dialogRef} className="file-fullscreen" aria-label="Full screen file preview" onClose={() => { setFullscreen(false); }}>
        <header><strong>{selected?.filename}</strong><button aria-label="Close full screen" onClick={() => { dialogRef.current?.close(); }}><X size={22} /></button></header>
        {fullscreen && selected ? <div className="file-controls"><FileSaveButton file={selected} />
          {canManage ? <>
            {!archived ? <button disabled={busy || !selected.url} onClick={() => { void manage(selected, selected.current ? "clear-current" : "set-current"); }}><Star size={15} />{selected.current ? "Unmark current" : "Make current schedule"}</button> : null}
            <button disabled={busy} onClick={() => { void manage(selected, archived ? "restore" : "archive").then(changed => { if (changed) dialogRef.current?.close(); }); }}>{archived ? <RotateCcw size={15} /> : <Archive size={15} />}{archived ? "Restore" : "Archive"}</button>
          </> : null}
        </div> : null}
        {fullscreen && error ? <p className="file-error" role="alert">{error}</p> : null}
        <div className="file-fullscreen-body">{fullscreen && selected ? <FilePreview file={selected} /> : null}</div>
      </dialog>
    </div>
  );
}

export function FilePreviewDialog({ file, onClose }: { file: PreviewFile | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (file && dialog && !dialog.open) dialog.showModal();
    else if (!file && dialog?.open) dialog.close();
  }, [file]);
  return <dialog ref={ref} className="file-fullscreen" aria-label="Full screen file preview" onClose={onClose}>
    <header><strong>{file?.filename}</strong><button type="button" aria-label="Close full screen" onClick={() => { ref.current?.close(); }}><X size={22} /></button></header>
    {file ? <><div className="file-controls"><FileSaveButton file={file} /></div><div className="file-fullscreen-body"><FilePreview file={file} /></div></> : null}
  </dialog>;
}
